import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { OtpChannel, SendOtp, LoadOrgStatus } from '@/modules/auth/use-cases'
import {
  ACCOUNT_LOAD_RETRY_DELAY_MS,
  EmailLinkedToDifferentAccountError,
  NetworkError,
  OtpSendFailedError,
  PhoneLinkedToDifferentAccountError,
  UserNotFoundError,
  canUseCommandCenter,
  isOtpSendRefusal,
  retryOnce,
} from '@/shared/domain'
import { isPhoneLoginEnabled } from '@/shared/config'
import { reportError } from '@/shared/infra/observability'
import { useOtpSendCooldown, useSubmitGuard } from '@/shared/ui/hooks'
import { isValidEmail } from '@/modules/auth/ui/validation'
import { validateRedirectUrl } from '@/modules/auth/ui/validate-redirect'

export type SetTokenOverride = (token: string) => void
export type ClearTokenOverride = () => void

export type UseSignInPageProps = {
  onSendOtp: SendOtp
  onLoadOrgStatus: LoadOrgStatus
  onSetTokenOverride: SetTokenOverride
  onClearTokenOverride: ClearTokenOverride
}

export type SignInStep = 'identifier' | 'otp' | 'checking'

const USER_NOT_FOUND_COOLDOWN_SECONDS = 8

export type SignInMode = Extract<OtpChannel, 'email' | 'phone'>

export type UseSignInPageResult = {
  step: SignInStep
  currentMode: SignInMode
  email: string
  phoneE164: string
  identifier: string
  isSubmitting: boolean
  isCheckingStatus: boolean
  isPhoneLoginEnabled: boolean
  cooldownSeconds: number
  isCooldownActive: boolean
  hasError: boolean
  errorMessage: string
  fieldError: string
  headline: string
  subtitle: string
  showCreateClubFooter: boolean
  setEmail: (v: string) => void
  setPhone: (e164: string, valid: boolean) => void
  setMode: (m: SignInMode) => void
  handleBlur: () => void
  handleSubmit: (e: FormEvent) => void
  backToIdentifier: () => void
  handleOtpSuccess: (accessToken: string) => Promise<void>
}

export function useSignInPage({
  onSendOtp,
  onLoadOrgStatus,
  onSetTokenOverride,
  onClearTokenOverride,
}: UseSignInPageProps): UseSignInPageResult {
  const [step, setStep] = useState<SignInStep>('identifier')
  const [currentMode, setCurrentMode] = useState<SignInMode>('email')
  const [email, setEmail] = useState<string>('')
  const [phoneE164, setPhoneE164] = useState<string>('')
  const [phoneValid, setPhoneValid] = useState<boolean>(false)
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasTouched, setHasTouched] = useState<boolean>(false)
  const [fieldError, setFieldError] = useState<string>('')
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (cooldownSeconds <= 0) return
    const timer = setTimeout(() => {
      setCooldownSeconds((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearTimeout(timer)
  }, [cooldownSeconds])

  const phoneEnabled = isPhoneLoginEnabled()
  const identifier = currentMode === 'phone' ? phoneE164 : email.trim()
  const {
    secondsLeft: sendCooldownSeconds,
    applyRefusal,
    recordSend,
  } = useOtpSendCooldown({
    channel: currentMode,
    identifier,
  })
  const activeCooldownSeconds = Math.max(cooldownSeconds, sendCooldownSeconds)

  const source = searchParams.get('source')
  const flow = searchParams.get('flow')
  const isMobileCreateOrg = source === 'mobile' && flow === 'create-org'
  const headline = isMobileCreateOrg ? 'Sign in to continue' : 'Welcome back'
  const subtitle = isMobileCreateOrg
    ? 'Verify your identity to set up your organization.'
    : 'Sign in to your BlindBook account'
  const showCreateClubFooter = source !== 'mobile'

  const validateField = useCallback(
    (mode: SignInMode, emailValue: string, phoneValue: string, phoneIsValid: boolean): string => {
      if (mode === 'phone') {
        if (phoneValue === '' || !phoneIsValid) return 'Enter a valid phone number'
        return ''
      }
      const trimmed = emailValue.trim()
      if (trimmed === '') return 'Enter your email'
      if (!isValidEmail(trimmed)) return 'Enter a valid email'
      return ''
    },
    [],
  )

  const updateEmail = useCallback(
    (v: string) => {
      setEmail(v)
      if (hasTouched) {
        setFieldError(validateField('email', v, phoneE164, phoneValid))
      }
    },
    [hasTouched, phoneE164, phoneValid, validateField],
  )

  const setPhone = useCallback(
    (e164: string, valid: boolean) => {
      setPhoneE164(e164)
      setPhoneValid(valid)
      if (hasTouched) {
        setFieldError(validateField('phone', email, e164, valid))
      }
    },
    [email, hasTouched, validateField],
  )

  const setMode = useCallback((m: SignInMode) => {
    setCurrentMode(m)
    setErrorMessage('')
    setFieldError('')
    setHasTouched(false)
  }, [])

  const handleBlur = useCallback(() => {
    setHasTouched(true)
    setFieldError(validateField(currentMode, email, phoneE164, phoneValid))
  }, [currentMode, email, phoneE164, phoneValid, validateField])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (activeCooldownSeconds > 0) return
    setHasTouched(true)
    const validationError = validateField(currentMode, email, phoneE164, phoneValid)
    if (validationError !== '') {
      setFieldError(validationError)
      return
    }
    setFieldError('')
    setErrorMessage('')
    await runGuarded(async () => {
      try {
        await onSendOtp({ identifier, channel: currentMode })
        recordSend()
        setStep('otp')
      } catch (err) {
        if (err instanceof UserNotFoundError) {
          if (currentMode === 'phone') {
            setErrorMessage('No account found for this phone number. Contact your club manager for an invitation.')
          } else {
            setErrorMessage('No account found for this email. Contact your club manager for an invitation.')
          }
          setCooldownSeconds(USER_NOT_FOUND_COOLDOWN_SECONDS)
        } else if (
          err instanceof EmailLinkedToDifferentAccountError ||
          err instanceof PhoneLinkedToDifferentAccountError
        ) {
          setErrorMessage(err.message)
        } else if (isOtpSendRefusal(err)) {
          setErrorMessage(err.message)
          applyRefusal(err)
        } else if (err instanceof OtpSendFailedError) {
          setErrorMessage(err.message)
        } else if (err instanceof NetworkError) {
          setErrorMessage('Network error. Please try again.')
        } else {
          setErrorMessage('Something went wrong. Please try again.')
        }
      }
    })
  }

  const backToIdentifier = () => {
    setStep('identifier')
    setErrorMessage('')
    setFieldError('')
    setHasTouched(false)
  }

  const handleOtpSuccess = useCallback(
    async (accessToken: string) => {
      setStep('checking')
      onSetTokenOverride(accessToken)

      const redirectTo = searchParams.get('redirectTo')
      const source = searchParams.get('source')
      if (source === 'mobile') {
        const safeUrl = validateRedirectUrl(redirectTo)
        if (safeUrl) {
          onClearTokenOverride()
          window.location.href = safeUrl
          return
        }
      }

      try {
        const orgStatus = await retryOnce(onLoadOrgStatus, ACCOUNT_LOAD_RETRY_DELAY_MS)
        onClearTokenOverride()

        if (canUseCommandCenter(orgStatus)) {
          window.location.href = '/dashboard'
          return
        }

        if (orgStatus.hasPendingOwnerInvitations) {
          window.location.href =
            orgStatus.pendingOwnerInvitations.length === 1
              ? `/join/${orgStatus.pendingOwnerInvitations[0].token}`
              : '/dashboard'
          return
        }

        window.location.href = '/no-access'
      } catch (err) {
        onClearTokenOverride()
        reportError(err, { fallbackMessage: 'Failed to check account access after sign-in.' })
        window.location.href = '/dashboard'
      }
    },
    [onClearTokenOverride, onLoadOrgStatus, onSetTokenOverride, searchParams],
  )

  return {
    step,
    currentMode,
    email,
    phoneE164,
    identifier,
    isSubmitting,
    isCheckingStatus: step === 'checking',
    isPhoneLoginEnabled: phoneEnabled,
    cooldownSeconds: activeCooldownSeconds,
    isCooldownActive: activeCooldownSeconds > 0,
    hasError: errorMessage !== '' || fieldError !== '',
    errorMessage,
    fieldError,
    headline,
    subtitle,
    showCreateClubFooter,
    setEmail: updateEmail,
    setPhone,
    setMode,
    handleBlur,
    handleSubmit,
    backToIdentifier,
    handleOtpSuccess,
  }
}
